/*
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { showToast } from "components/AppToast"
import { setRecalculationFailed, setResultsTables } from "features/results/ResultsSlice"
import { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { computeResults } from "services/computations"
import { Sentry } from "services/sentry"

import {
  createRecalculationScheduler,
  type RecalculationObservation,
} from "./recalculationScheduler"

/**
 * Drives the results recalculation for one mounted Results component: coalesces
 * drag-frequency filter changes onto the trailing edge, recalculates immediately when
 * new data arrives, and computes plus dispatches the result.
 *
 * The scheduling decisions themselves live in `recalculationScheduler.ts`, which knows
 * nothing about React or Redux; this hook is only the wiring.
 */
export const useCoalescedRecalculation = (observation: RecalculationObservation): void => {
  const dispatch = useDispatch()

  // AIDEV-NOTE: (#540) Created exactly once per mount. `useState`'s initialiser carries
  // that guarantee; `useMemo` does not (React reserves the right to discard memoised
  // values), and a scheduler rebuilt mid-drag would take its pending trailing call with
  // it. `dispatch` is captured here rather than passed per-pass because react-redux
  // returns the store's own stable dispatch.
  const [scheduler] = useState(() =>
    createRecalculationScheduler(
      inputs => {
        const newResults = computeResults(
          inputs.userParameters,
          inputs.historicalDataFilters,
          inputs.initialScrsData
        )
        dispatch(setResultsTables(newResults))
        // handleTableRendered will subsequently record the tables as being fresh
      },
      // AIDEV-NOTE: (#1069) Reached for any recalculation whose throw has nowhere useful
      // to go - see the `throwsToBoundary` note in recalculationScheduler.ts. That is
      // mostly the trailing-edge and maxWait invocations, which have no caller at all,
      // but the unmount flush routes here too despite having one. Rethrowing
      // here would be worse than useless: the trailing-edge call runs from a setTimeout,
      // and no React error boundary can see a throw from a timer callback, so the user
      // would be left with the previous cohort's figures under the new filter selection
      // and nothing on screen saying so.
      //
      // The shape mirrors the submission-path catch in UserParametersForm ("Calculation
      // Error"): report with a distinguishing errorType, and show a message that does
      // not carry the exception text, which is an internal detail and can name columns
      // and values the user never entered.
      error => {
        Sentry.captureException(error, {
          tags: { errorType: "recalculation_error", component: "useCoalescedRecalculation" },
        })

        showToast({
          type: "error",
          title: "Calculation Error",
          message: "Failed to update results for the current filters. Please try again.",
        })

        // The toast auto-hides after five seconds; this is what keeps the tables from
        // reading as current once it has gone.
        dispatch(setRecalculationFailed())
      }
    )
  )

  // AIDEV-NOTE: (#1251) Read through a ref, and deliberately absent from the effect's
  // dependencies below. The scheduler consults this flag only on its first observation after a
  // mount, so a change of value never needs to re-run the effect - and making it a dependency
  // is actively harmful: `handleDeferredError` dispatches `setRecalculationFailed` on a failed
  // recalculation, which would then flip the flag, re-run the effect and re-arm the very
  // calculation that just threw, against unchanged inputs. `fresh` was already false at that
  // point, so no other dependency changes there.
  const recalculationFailedRef = useRef(false)

  const {
    userParameters,
    historicalDataFilters,
    initialScrsData,
    initialScrsDataReady,
    filtersInitialized,
    uiTablesCalculationPending,
    recalculationFailed,
  } = observation

  // AIDEV-NOTE: (#1251 review) Synced in an effect, not written during render: a render React
  // throws away would otherwise leave the ref holding a value nothing committed, and
  // eslint-plugin-react-hooks 7 rejects a render-time ref write (react-hooks/refs). No
  // dependency list, so it runs after every commit. Its position is load-bearing: React runs
  // a component's effects in declaration order within a commit, so declaring this one first
  // is what guarantees the scheduler effect below reads this commit's value rather than the
  // previous one. Moved below that effect, it would hand the scheduler a stale flag in any
  // commit where the flag changes together with one of the scheduler effect's dependencies.
  //
  // `latestContextRef` in UserParametersForm is still written during render. The plugin does
  // not flag it only because the React Compiler skips that whole component (its
  // `formMethods.watch` call is a known-incompatible react-hook-form API), not because the
  // pattern is acceptable there.
  useEffect(() => {
    recalculationFailedRef.current = recalculationFailed
  })

  // Every pass reaches the scheduler, including the ones that cannot recalculate: it
  // compares source identities against the previous pass to spot superseded data.
  useEffect(() => {
    scheduler.observeInputs({
      userParameters,
      historicalDataFilters,
      initialScrsData,
      initialScrsDataReady,
      filtersInitialized,
      uiTablesCalculationPending,
      recalculationFailed: recalculationFailedRef.current,
    })
  }, [
    scheduler,
    userParameters,
    historicalDataFilters,
    initialScrsData,
    initialScrsDataReady,
    filtersInitialized,
    uiTablesCalculationPending,
  ])

  // AIDEV-NOTE: (#540) Flush rather than cancel, the opposite of what a change of source
  // data does. A pending trailing recalculation discarded at unmount would leave the last
  // dispatched results describing filter values the user has already moved away from, with
  // nothing on screen saying so: that call is merely late. A call superseded by new data is
  // wrong rather than late, so the scheduler cancels that one instead.
  useEffect(() => {
    return () => {
      scheduler.flushPending()
    }
  }, [scheduler])
}
